import { capitalize } from 'lodash/fp';
import { Controller, useFormState } from 'react-hook-form';
import { Form, reduxForm } from 'redux-form';
import { FormError } from 'shared/components/FormControl';

import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { CreateExternalIntegrationParams } from 'dashboard/redux/thunk-actions/external-integrations';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { LegacyExternalIntegrationVendor } from 'lib/constants/externalIntegrationVendors';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface FormExternalIntegrationAddApiKeyProps {
  externalIntegrationVendor: LegacyExternalIntegrationVendor;
  handleClose: () => void;
  createExternalIntegration: (
    externalIntegrationType: ExternalIntegration['type'],
    integrationParams: CreateExternalIntegrationParams,
  ) => void;
}

interface FormExternalIntegrationAddApiKeyData {
  name: string;
  'api-key'?: string;
  code?: string;
  'client-id'?: string;
  'integration-environment'?: string;
  'member-number'?: string;
  'security-code'?: string;
  'webhook-secret'?: string;
}

type CreateExternalIntegrationApiKeyParams = Record<
  keyof FormExternalIntegrationAddApiKeyData,
  string
>;

const FormExternalIntegrationAddApiKey = ({
  externalIntegrationVendor,
  createExternalIntegration,
  handleClose,
}: FormExternalIntegrationAddApiKeyProps) => {
  const { register, handleSubmit, control, reset } =
    useFormSecurely<FormExternalIntegrationAddApiKeyData>({
      mode: 'onBlur',
    });

  const onSubmit = handleSubmit((data: FormExternalIntegrationAddApiKeyData) => {
    const createParams = externalIntegrationVendor[
      'input-fields'
    ].reduce<CreateExternalIntegrationApiKeyParams>((res, inputField) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      res[inputField.key as keyof FormExternalIntegrationAddApiKeyData] =
        data[inputField.key as keyof FormExternalIntegrationAddApiKeyData];
      return res;
    }, {} as CreateExternalIntegrationApiKeyParams);

    createExternalIntegration(
      externalIntegrationVendor.model as ExternalIntegration['type'],
      createParams,
    );
    handleClose();
    reset();
  });

  const { isSubmitting, isDirty, errors } = useFormState({
    control,
  });

  const submitDisabled = !isDirty || isSubmitting || !!Object.keys(errors).length;

  const renderFormInput = (inputField: FieldSchema) => {
    switch (inputField.type) {
      case FieldSchemaType.String: {
        return (
          <Input
            {...register(inputField.key as keyof FormExternalIntegrationAddApiKeyData, {
              required: `${inputField.label} is required`,
            })}
            type='text'
            placeholder={inputField.label}
            hasError={!!errors[inputField.key as keyof FormExternalIntegrationAddApiKeyData]}
            disabled={isSubmitting}
            aria-label={inputField.label}
          />
        );
      }
      case FieldSchemaType.Choices: {
        return (
          <Controller
            control={control}
            name={inputField.key as keyof FormExternalIntegrationAddApiKeyData}
            rules={{ required: `${inputField.label} must be selected` }}
            render={({ field: { onChange, value } }) => (
              <Select
                id={inputField.key}
                value={value}
                onValueChange={onChange}
                placeholder={`Select the ${inputField.label}`}
                hasError={!!errors[inputField.key as keyof FormExternalIntegrationAddApiKeyData]}
              >
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                {inputField.config.options.map((option) => (
                  <Option key={option} value={option}>
                    {capitalize(option)}
                  </Option>
                ))}
              </Select>
            )}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <ModalContainer>
        <ModalHeader onClose={handleClose}>
          Add {externalIntegrationVendor['display-name']} API Key
        </ModalHeader>
        <ModalBody>
          {externalIntegrationVendor['input-fields'].map((inputField: FieldSchema) => (
            <>
              <Label size='lg' weight='bold'>
                {inputField.label}
              </Label>
              <Spacer height={1} />
              {renderFormInput(inputField)}
              {errors[inputField.key as keyof FormExternalIntegrationAddApiKeyData] && (
                <FormError>
                  {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                  {errors[inputField.key as keyof FormExternalIntegrationAddApiKeyData].message}
                </FormError>
              )}
              <Spacer height={2} />
            </>
          ))}
          <Spacer height={4} />
          <HStack equal={true}>
            <Button
              id='add-api-key__cancel-button'
              type='button'
              variant='basic'
              onClick={handleClose}
              aria-label='cancel button'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='add-api-key__create-button'
              aria-label='create button'
              disabled={submitDisabled}
            >
              Create
            </Button>
          </HStack>
        </ModalBody>
      </ModalContainer>
    </Form>
  );
};

export default reduxForm<
  FormExternalIntegrationAddApiKeyData,
  FormExternalIntegrationAddApiKeyProps
>({
  form: 'newExternalIntegrationApiKey',
})(FormExternalIntegrationAddApiKey);
